import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, ChevronRight, Clock, ExternalLink, MapPin, Search, TrainFront } from 'lucide-react';
import './HowToReach.css';
import './CjpProtest.css';

/* ─────────────────────────────────────────────────────────────────────────
   /cjp-protest-2-0-delhi
   News-style explainer for the Cockroach Janta Party (CJP) "Protest 2.0" in
   Delhi on 10 October 2026, plus live all-mode route links
   to Jantar Mantar from key cities. Facts are from published reports; keep
   STATUS_UPDATED and the status copy current as the story moves.
   ───────────────────────────────────────────────────────────────────────── */

const PAGE_URL = 'https://routechef.com/cjp-protest-2-0-delhi';
const STATUS_UPDATED = '5 October 2026';

// Destination: Jantar Mantar, New Delhi (raw coordinates, same shape as corridor pages)
const VENUE = { name: 'Jantar Mantar, New Delhi', lat: 28.6304, lng: 77.2249 };

const ORIGIN_GROUPS = [
    {
        region: 'Maharashtra',
        cities: [
            ['Pune', 18.5204, 73.8567],
            ['Nashik', 19.9975, 73.7898],
            ['Nagpur', 21.1458, 79.0882],
            ['Aurangabad', 19.8762, 75.3433],
            ['Kolhapur', 16.705, 74.2433],
            ['Solapur', 17.6599, 75.9064],
            ['Amravati', 20.9374, 77.7796],
        ],
    },
    {
        region: 'North India',
        cities: [
            ['Delhi', 28.6139, 77.209],
            ['Jaipur', 26.9124, 75.7873],
            ['Lucknow', 26.8467, 80.9462],
            ['Chandigarh', 30.7333, 76.7794],
            ['Varanasi', 25.3176, 82.9739],
            ['Dehradun', 30.3165, 78.0322],
            ['Amritsar', 31.634, 74.8723],
        ],
    },
    {
        region: 'West & Central India',
        cities: [
            ['Ahmedabad', 23.0225, 72.5714],
            ['Surat', 21.1702, 72.8311],
            ['Vadodara', 22.3072, 73.1812],
            ['Goa', 15.4909, 73.8278],
            ['Indore', 22.7196, 75.8577],
            ['Bhopal', 23.2599, 77.4126],
            ['Jabalpur', 23.1815, 79.9864],
            ['Raipur', 21.2514, 81.6296],
        ],
    },
    {
        region: 'South India',
        cities: [
            ['Bangalore', 12.9716, 77.5946],
            ['Hyderabad', 17.385, 78.4867],
            ['Chennai', 13.0827, 80.2707],
            ['Kochi', 9.9312, 76.2673],
            ['Trivandrum', 8.5241, 76.9366],
            ['Mangalore', 12.9141, 74.856],
            ['Visakhapatnam', 17.6868, 83.2185],
        ],
    },
    {
        region: 'East & Northeast India',
        cities: [
            ['Kolkata', 22.5726, 88.3639],
            ['Patna', 25.5941, 85.1376],
            ['Ranchi', 23.3441, 85.3096],
            ['Bhubaneswar', 20.2961, 85.8245],
            ['Guwahati', 26.1445, 91.7362],
        ],
    },
];

const TRAVEL_DATES = [
    { value: '2026-10-08', label: 'Thu, 8 Oct' },
    { value: '2026-10-09', label: 'Fri, 9 Oct' },
    { value: '2026-10-10', label: 'Sat, 10 Oct' },
];

const FAQS = [
    {
        q: 'Where is the CJP protest in Delhi on 10 October 2026?',
        a: 'CJP has announced a fresh protest at Jantar Mantar in New Delhi on 10 October 2026. The announcement followed the group\'s 10 October mobilisation in Delhi. Check CJP\'s latest official channels and Delhi Police advisories before travelling because permissions and access arrangements can change.',
    },
    {
        q: 'When is the CJP protest in Delhi?',
        a: 'Saturday, 10 October 2026. Current reports identify Jantar Mantar as the planned venue, but an exact start time is not clearly confirmed in the reports reviewed for this page.',
    },
    {
        q: 'Why is CJP protesting in Delhi on 10 October?',
        a: 'CJP is demanding the resignation of Chief Election Commissioner Gyanesh Kumar and is protesting over alleged irregularities in the Special Intensive Revision (SIR) of electoral rolls. The Election Commission and government have rejected allegations of wrongdoing.',
    },
    {
        q: 'What is Jantar Mantar 2.0?',
        a: 'CJP founder Abhijeet Dipke described the 10 October mobilisation as a return to Jantar Mantar after the group\'s earlier Delhi protest campaign. Media reports have referred to the new mobilisation as "Jantar Mantar 2.0".',
    },
    {
        q: 'Who is leading the CJP protest?',
        a: 'CJP founder Abhijeet Dipke announced the 10 October Delhi protest. Recent reports also name co-convenor Ashutosh Ranka and spokesperson Saurav Das among the movement\'s prominent leaders.',
    },
    {
        q: 'How do I reach Jantar Mantar?',
        a: 'New Delhi Railway Station is roughly 2.5 km from Jantar Mantar. Shivaji Stadium Metro is around 2 km away, while Delhi Airport is roughly 18 km away by road. Use the live route links on this page to compare trains, buses, flights and mixed-mode journeys from your city.',
    },
];

const SOURCES = [
    { title: '“Chalo Delhi”: CJP announces October 10 protest at Jantar Mantar', outlet: 'NDTV / PTI', url: 'https://www.ndtv.com/india-news/chalo-delhi-cjp-leader-abhijeet-dipke-announces-protest-on-october-10-against-chief-election-commissioner-gyanesh-kumar-12132214' },
    { title: 'CJP announces Jantar Mantar protest on October 10', outlet: 'India Today', url: 'https://www.indiatoday.in/india/story/cockroach-janta-party-cjp-delhi-jantar-mantar-protest-cec-gyanesh-kumar-3008537-2026-10-03' },
    { title: 'CJP leaders call for nationwide mobilisation on October 10', outlet: 'The New Indian Express', url: 'https://www.newindianexpress.com/india/2026/Oct/05/cjp-vows-protests-will-continue-despite-possible-arrests-calls-for-nationwide-mobilisation-on-october-10' },
    { title: 'CJP leaders claim arrests planned ahead of October 10 protest', outlet: 'The Indian Express', url: 'https://indianexpress.com/article/cities/delhi/cjp-leaders-arrested-october-10-delhi-protest-saurav-das-10907084/' },
    { title: 'CJP founder Abhijeet Dipke on X', outlet: 'X', url: 'https://x.com/abhijeet_dipke' },
];

const liveSearchUrl = (fromName, fromLat, fromLng, date) => {
    const p = new URLSearchParams({
        from: fromName,
        scode: '',
        stype: 'google',
        to: VENUE.name,
        dcode: '',
        dtype: 'google',
        date,
        adults: 1,
        children: 0,
        infants: 0,
        seatClasses: 'Economy,3A',
        modes: 'All',
        nonstop: 0,
        slat: fromLat,
        slng: fromLng,
        dlat: VENUE.lat,
        dlng: VENUE.lng,
    });
    return `/search-form?${p.toString()}`;
};

const CjpProtestDelhi = () => {
    const [travelDate, setTravelDate] = useState('2026-10-10');

    const pageTitle = 'CJP Protest Delhi: Jantar Mantar, 10 October 2026 & How to Reach | RouteChef';
    const metaDescription =
        'Cockroach Janta Party (CJP) protest in Delhi at Jantar Mantar on 10 October 2026: venue, timing, latest status, demands, and live train, bus and flight routes to Delhi from cities across India.';

    const originCount = useMemo(() => ORIGIN_GROUPS.reduce((n, g) => n + g.cities.length, 0), []);

    useEffect(() => {
        document.title = pageTitle;
        const ensureMeta = (attr, key, content) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        ensureMeta('name', 'description', metaDescription);
        ensureMeta('property', 'og:title', pageTitle);
        ensureMeta('property', 'og:description', metaDescription);
        ensureMeta('property', 'og:type', 'article');
        ensureMeta('property', 'og:url', PAGE_URL);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        const hadCanonical = !!canonical;
        const prevCanonical = canonical?.href;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.href = PAGE_URL;

        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'cjp-protest-jsonld';
        ld.text = JSON.stringify([
            {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: 'CJP Protest in Delhi on 10 October: Jantar Mantar, latest status and how to reach',
                description: metaDescription,
                datePublished: '2026-10-03',
                dateModified: '2026-10-05',
                mainEntityOfPage: PAGE_URL,
                publisher: { '@type': 'Organization', name: 'RouteChef', url: 'https://routechef.com/' },
                about: [
                    { '@type': 'Organization', name: 'Cockroach Janta Party', alternateName: ['CJP', 'Cockroach Janata Party'] },
                    { '@type': 'Place', name: 'Jantar Mantar', address: 'New Delhi, Delhi, India' },
                ],
            },
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: FAQS.map((f) => ({
                    '@type': 'Question',
                    name: f.q,
                    acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://routechef.com/' },
                    { '@type': 'ListItem', position: 2, name: 'CJP Protest 2.0 Delhi', item: PAGE_URL },
                ],
            },
        ]);
        document.head.appendChild(ld);

        return () => {
            document.getElementById('cjp-protest-jsonld')?.remove();
            if (!hadCanonical) canonical.remove();
            else if (prevCanonical) canonical.href = prevCanonical;
        };
    }, [pageTitle, metaDescription]);

    return (
        <div className="htr-page">
            {/* ── Hero ── */}
            <header className="htr-hero">
                <nav className="htr-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <ChevronRight size={14} />
                    <span className="htr-crumb-current">CJP Protest 2.0 Delhi</span>
                </nav>
                <h1>CJP Protest in Delhi on 10 October: Jantar Mantar and How to Reach</h1>
                <p className="htr-subtitle">
                    Everything travellers need on the Cockroach Janta Party protest in Delhi: where and when it is planned,
                    what CJP is demanding, the latest reported status, and live routes to Jantar Mantar from across India.
                </p>

                <div className="cjp-facts" aria-label="Key facts">
                    <div className="cjp-fact">
                        <CalendarDays size={18} />
                        <div>
                            <span className="cjp-fact-label">Date</span>
                            <strong>Sat, 10 October 2026</strong>
                            <span className="cjp-fact-sub">Nationwide mobilisation</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <MapPin size={18} />
                        <div>
                            <span className="cjp-fact-label">Planned venue</span>
                            <strong>Jantar Mantar</strong>
                            <span className="cjp-fact-sub">New Delhi</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <Clock size={18} />
                        <div>
                            <span className="cjp-fact-label">Time</span>
                            <strong>10 October</strong>
                            <span className="cjp-fact-sub">Exact start time not clearly confirmed in current reports</span>
                        </div>
                    </div>
                    <div className="cjp-fact">
                        <TrainFront size={18} />
                        <div>
                            <span className="cjp-fact-label">Nearest station</span>
                            <strong>New Delhi</strong>
                            <span className="cjp-fact-sub">~2.5 km from Jantar Mantar</span>
                        </div>
                    </div>
                </div>

                {/* Status box: the single most important thing for anyone travelling */}
                <div className="cjp-status" role="status">
                    <div className="cjp-status-head">
                        <AlertTriangle size={18} />
                        <span>Delhi protest status, updated {STATUS_UPDATED}</span>
                    </div>
                    <p>
                        CJP has announced a <strong>10 October 2026</strong> protest at <strong>Jantar Mantar, New Delhi</strong>,
                        following its 10 October mobilisation in Delhi. Current reporting describes it as a peaceful protest
                        seeking the resignation of Chief Election Commissioner Gyanesh Kumar over alleged irregularities in the
                        Special Intensive Revision (SIR) of electoral rolls. Delhi Police restrictions, permissions and the final
                        on-ground arrangements can change, so confirm the latest advisories before travelling.
                    </p>
                </div>
            </header>

            <main className="htr-content">
                {/* ── Live routes ── */}
                <section className="htr-section" id="live-routes" aria-labelledby="routes-heading">
                    <h2 id="routes-heading">Live routes to Jantar Mantar, Delhi from {originCount} cities</h2>
                    <p className="htr-para">
                        Each link opens a live RouteChef search from your city straight to Jantar Mantar, comparing trains,
                        flights, buses and mixed-mode combinations in one place. the October 10 mobilisation is a national holiday, so
                        trains and flights into Delhi around 1 and 10 October are likely to fill up early.
                    </p>

                    <div className="cjp-date-picker" role="radiogroup" aria-label="Travel date">
                        <span className="cjp-date-label">Travelling on</span>
                        {TRAVEL_DATES.map((d) => (
                            <button
                                key={d.value}
                                type="button"
                                role="radio"
                                aria-checked={travelDate === d.value}
                                className={`htr-mode-tab${travelDate === d.value ? ' active' : ''}`}
                                onClick={() => setTravelDate(d.value)}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>

                    {ORIGIN_GROUPS.map((group) => (
                        <div key={group.region} className="cjp-region">
                            <h3>{group.region}</h3>
                            <div className="cjp-origin-grid">
                                {group.cities.map(([name, lat, lng]) => (
                                    <Link
                                        key={name}
                                        to={liveSearchUrl(name, lat, lng, travelDate)}
                                        className="cjp-origin-link"
                                        rel="nofollow"
                                    >
                                        <span>{name} <ChevronRight size={14} className="htr-index-arrow" /> Delhi</span>
                                        <Search size={15} />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>

                {/* ── What's happening ── */}
                <section className="htr-section" aria-labelledby="what-heading">
                    <h2 id="what-heading">What is the CJP October 10 protest?</h2>
                    <p className="htr-para">
                        The Cockroach Janta Party (CJP) has announced a major Delhi mobilisation on 10 October 2026 at
                        Jantar Mantar. The protest follows the group&apos;s 2 October demonstration in Mumbai and is focused
                        on the functioning of the Election Commission of India, particularly allegations surrounding the
                        Special Intensive Revision (SIR) of electoral rolls.
                    </p>
                    <p className="htr-para">
                        CJP founder Abhijeet Dipke has called for a peaceful "Chalo Delhi" mobilisation and said supporters
                        from across India would gather in the capital. Recent reports also identify Ashutosh Ranka and Saurav Das
                        among the movement&apos;s prominent leaders.
                    </p>
                </section>

                {/* ── the October 10 mobilisation ── */}
                <section className="htr-section" aria-labelledby="gandhi-heading">
                    <h2 id="gandhi-heading">Why 10 October?</h2>
                    <p className="htr-para">
                        The October 10 mobilisation was announced after CJP&apos;s 2 October protest in Mumbai. Media reports
                        describe the Delhi event as a fresh Jantar Mantar protest if Chief Election Commissioner Gyanesh Kumar
                        does not resign. CJP says the agitation will remain peaceful and is linked to its demand for free and fair elections.
                    </p>
                </section>

                {/* ── Permission ── */}
                <section className="htr-section" aria-labelledby="permission-heading">
                    <h2 id="permission-heading">Delhi protest status and what travellers should know</h2>
                    <p className="htr-para">
                        Current reporting confirms the announced venue as Jantar Mantar, but permissions, crowd-management
                        arrangements and access restrictions can change as the event approaches. Delhi has already seen
                        detentions and tighter security around recent demonstrations linked to the same issue.
                    </p>
                    <ul className="cjp-list">
                        <li>Check Delhi Police advisories before travelling into central Delhi</li>
                        <li>Expect possible barricading, road diversions and restricted access near Parliament Street and Jantar Mantar</li>
                        <li>Allow additional time for last-mile travel from New Delhi Railway Station or the airport</li>
                        <li>Do not rely on an old protest timing or route plan if organisers or authorities issue an update</li>
                    </ul>
                    <p className="htr-para">
                        The event is political and may involve large crowds. RouteChef&apos;s role on this page is limited to
                        travel planning; verify the event details with official sources before you leave.
                    </p>
                </section>

                {/* ── Getting to the venue ── */}
                <section className="htr-section" aria-labelledby="venue-heading">
                    <h2 id="venue-heading">Getting to Jantar Mantar once you are in Delhi</h2>
                    <div className="htr-table-wrap">
                        <table className="htr-table">
                            <thead>
                                <tr>
                                    <th>Arrival point</th>
                                    <th>Type</th>
                                    <th>Distance to Jantar Mantar</th>
                                    <th>Onward</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td>New Delhi (NDLS)</td><td>Railway Station</td><td><strong>~2.5 km</strong></td><td>Short cab/auto or Metro</td></tr>
                                <tr><td>Shivaji Stadium</td><td>Metro Station</td><td><strong>~2 km</strong></td><td>Short walk/cab</td></tr>
                                <tr><td>Old Delhi (DLI)</td><td>Railway Station</td><td><strong>~4.5 km</strong></td><td>Cab/Metro</td></tr>
                                <tr><td>Anand Vihar (ANVR)</td><td>Railway Station</td><td><strong>~13 km</strong></td><td>Metro or cab</td></tr>
                                <tr><td>Delhi Airport (DEL)</td><td>Airport</td><td><strong>~18 km</strong></td><td>Airport Express / cab</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="htr-para cjp-note">
                        Delhi Police may impose access controls, barricading, traffic diversions or restrictions around
                        central Delhi during large demonstrations. Allow extra time and verify the latest travel advisories
                        before heading to Jantar Mantar.
                    </p>
                </section>

                {/* ── FAQ ── */}
                <section className="htr-section" aria-labelledby="faq-heading">
                    <h2 id="faq-heading">CJP protest: frequently asked questions</h2>
                    <div className="htr-faqs">
                        {FAQS.map((f, i) => (
                            <details key={f.q} className="htr-faq" open={i === 0}>
                                <summary>{f.q}</summary>
                                <p>{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                {/* ── Sources ── */}
                <section className="htr-section" aria-labelledby="sources-heading">
                    <h2 id="sources-heading">Sources</h2>
                    <ul className="cjp-sources">
                        {SOURCES.map((s) => (
                            <li key={s.url}>
                                <a href={s.url} target="_blank" rel="noopener noreferrer">
                                    {s.title} <ExternalLink size={13} />
                                </a>
                                <span className="cjp-source-outlet">{s.outlet}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* ── Bottom CTA ── */}
                <section className="htr-final-cta">
                    <h2>Travelling to Delhi for 10 October?</h2>
                    <p>Pick your city above, or search any route to Jantar Mantar with live trains, buses and flights.</p>
                    <a href="#live-routes" className="htr-search-btn large">
                        <Search size={18} />
                        Find my route to Jantar Mantar
                    </a>
                </section>

                <p className="htr-disclaimer">
                    RouteChef is a travel planning platform and is not affiliated with the Cockroach Janta Party or any
                    political organisation. This page summarises published news reports for travel planning; event details,
                    permissions and timings may change. Verify the latest information from official sources before travelling.
                    Last updated {STATUS_UPDATED}.
                </p>
            </main>
        </div>
    );
};

export default CjpProtestDelhi;
